import React from 'react';
import './style.scss';
import { loadScript } from '../../utils/browser';
import playIcon from '../../img/play.svg';
import closeIcon from '../../img/close.svg';
import condenseIcon from '../../img/condense.svg';
import { trackingService } from '../../utils/TrackingService';
import { WIDGET_EVENTS, WIDGET_SOURCE_APPS } from '../../utils/constants';
import { isActionKey } from '../../utils/keyboard';
import { LightWeightPlayerAdapter } from '../../utils/player';

const playbackTime = 10000;

export default class FloatingActionButton extends React.PureComponent {
  constructor(...args) {
    super(...args);

    this.state = {
      broadcastOrientation: undefined,
      hasError: false,
      presentCallToAction: false,
      isPlaying: false,
      canShowUI: false,
    };

    this._playerRef = React.createRef();
    this.onClickPlay = this.onClickPlay.bind(this);
    this.onClickClose = this.onClickClose.bind(this);
    this.onClickCondense = this.onClickCondense.bind(this);
    this.onKeyDownPlay = this.onKeyDownPlay.bind(this);
    this.onKeyDownCondense = this.onKeyDownCondense.bind(this);
    this.onKeyDownClose = this.onKeyDownClose.bind(this);
    this._playerTimeout = null;
  }

  componentDidMount() {
    this.checkShouldPlay();
  }

  componentDidUpdate(prevProps) {
    if (
      this.state.hasError &&
      (prevProps.previewResourceUri !== this.props.previewResourceUri ||
        prevProps.showId !== this.props.showId ||
        prevProps.isLive !== this.props.isLive ||
        prevProps.shouldUseLightWeightPlayer !== this.props.shouldUseLightWeightPlayer ||
        prevProps.widgetId !== this.props.widgetId)
    ) {
      this.setState({ hasError: false });
    }

    this.checkShouldPlay();
    if (this.props.isCondensed && !prevProps.isCondensed) {
      // isCondensed changed - might just as well do some cleaning
      this.cleanupPlayer();
    }
  }

  componentWillUnmount() {
    this.cleanupPlayer();
  }

  checkShouldPlay() {
    const { isLive, previewResourceUri, showId, shouldUseLightWeightPlayer, isCondensed, isVisible } = this.props;
    const { presentCallToAction } = this.state;
    if (!isVisible) return;
    const { isPlaying } = this.state;

    if (isLive) {
      if (previewResourceUri && !isCondensed && !isPlaying) {
        this.setState({ isPlaying: true });
        this.playBroadcast(previewResourceUri);
      } else {
        this.setupFadeTimer();
      }
    } else if (shouldUseLightWeightPlayer && !isCondensed && !isPlaying && !presentCallToAction) {
      this.setState({ isPlaying: true });
      this.playShow(showId);
    }
  }

  cleanupPlayer() {
    if (this._player && this._playerRef.current) {
      delete this._player._listeners;
      this._player.removeAllListeners?.();
      this._player.pause();
      this._player.destroy?.();
      this._currentResourceUri = null;
      this._player = null;
      this.setState({ broadcastOrientation: undefined, canShowUI: false });
    }
  }

  setupFadeTimer() {
    const { previewDuration = playbackTime } = this.props;
    this.clearPlaybackTimer();
    if (previewDuration === 0) return; // If previewDuration is set to 0, let preview run forever
    this._playerTimeout = setTimeout(() => {
      if (this._player) {
        this._player.pause();
        this.setState({ isPlaying: false });
      }
      this.setState({ presentCallToAction: true });
      this.clearPlaybackTimer();
    }, previewDuration);
  }

  clearPlaybackTimer() {
    if (this._playerTimeout) window.clearTimeout(this._playerTimeout);
    this._playerTimeout = null;
  }

  playShow(showId) {
    const playerNode = this._playerRef.current;

    if (!playerNode) {
      return;
    }

    this.cleanupPlayer();

    this._player = new LightWeightPlayerAdapter({ eventId: showId, node: playerNode });

    this._player.addEventListener('error', e => {
      if (this.props.showDebug) {
        console.error(`Couldn't launch player in FAB for showId: ${showId}`, e);
      }

      playerNode.style.opacity = 0;
      this.clearPlaybackTimer();
      this.cleanupPlayer();
      this.setState({ hasError: true });
    });

    this._player.addEventListener('playing', () => {
      this.setState({ canShowUI: true });
      playerNode.style.opacity = 1;
      this.setupFadeTimer();

      trackingService.track({
        actionOrigin: WIDGET_EVENTS.ACTION_ORIGIN.FLOATING_ACTION_BUTTON,
        eventType: WIDGET_EVENTS.ON_PLAY,
        sourceApp: WIDGET_SOURCE_APPS.FAB,
        orgId: this.props.orgId,
        widgetId: this.props.widgetId,
        showId,
        isLive: false,
        isPlaying: true,
        videoCurrentTime: 0,
      });
    });

    this._player.addEventListener('pause', () => {
      if (this._playerTimeout) {
        this.setState({ presentCallToAction: true });
        this.clearPlaybackTimer();
      }

      trackingService.track({
        actionOrigin: WIDGET_EVENTS.ACTION_ORIGIN.FLOATING_ACTION_BUTTON,
        eventType: WIDGET_EVENTS.ON_PAUSE,
        sourceApp: WIDGET_SOURCE_APPS.FAB,
        orgId: this.props.orgId,
        widgetId: this.props.widgetId,
        showId,
        isLive: false,
        isPlaying: false,
        videoCurrentTime: Math.floor(this._player.currentTime),
      });
    });

    this._player.addEventListener('broadcastData', ({ width, height }) => {
      this._player.scaleMode = 'aspectFill';
      this.setState({ broadcastOrientation: width > height ? 'LANDSCAPE' : 'PORTRAIT' });
    });

    // Track play event after 3 seconds
    let hasTracked = false;
    this._player.addEventListener('timeupdate', ({ currentTime }) => {
      if (!hasTracked && currentTime && currentTime >= 3) {
        trackingService.track({
          actionOrigin: WIDGET_EVENTS.ACTION_ORIGIN.FLOATING_ACTION_BUTTON,
          eventType: WIDGET_EVENTS.ON_PROGRESS,
          sourceApp: WIDGET_SOURCE_APPS.FAB,
          orgId: this.props.orgId,
          widgetId: this.props.widgetId,
          showId,
          isLive: false,
          isPlaying: this.state.isPlaying,
          videoCurrentTime: Math.floor(currentTime),
        });
        hasTracked = true;
      }
    });
  }

  async playBroadcast(resourceUri) {
    if (this._currentResourceUri !== resourceUri) {
      await loadScript('https://cdn.bambuser.net/player/lib/bambuser-video-iframeapi/latest/bambuser-video-iframeapi.min.js', 'baas-sdk-player');

      // Cleanup any previous player?
      this.cleanupPlayer();

      this.setState({ canShowUI: true });

      // Start a new player with muted playback!
      const playerNode = this._playerRef.current;
      this._currentResourceUri = resourceUri;
      this._player = BambuserPlayer.create(playerNode, resourceUri, {
        noFullscreen: true,
        usePreviewAsPoster: false,
      });
      this._player.controls = false;
      this._player.muted = true;
      this._player.scaleMode = 'aspectFill';

      let hasOnPlayEmitted = false; // We only want to emit on-play once
      let hasOnProgressEmitted = false; // We only want to emit on-progress once
      let playStartedTime = null; // Since for live videos currentTime is not always `0`, we need to track the time when the video started playing

      this._player.addEventListener('playing', () => {
        playerNode.style.opacity = 1;
        this.setupFadeTimer();
      });

      this._player.addEventListener('pause', () => {
        playerNode.style.opacity = 0;
        if (this._playerTimeout) {
          // okay, timer have not reached yet... lets help with state handling
          this.setState({ presentCallToAction: true });
          this.clearPlaybackTimer();
        }

        trackingService.track({
          actionOrigin: WIDGET_EVENTS.ACTION_ORIGIN.FLOATING_ACTION_BUTTON,
          eventType: WIDGET_EVENTS.ON_PAUSE,
          sourceApp: WIDGET_SOURCE_APPS.FAB,
          orgId: this.props.orgId,
          widgetId: this.props.widgetId,
          showId: this.props.showId,
          isLive: true,
          isPlaying: false,
          videoCurrentTime: Math.floor(this._player.currentTime),
        });
      });

      this._player.addEventListener('canplay', async () => {
        if (!hasOnPlayEmitted) {
          try {
            await this._player.play();

            const { showId, widgetId, orgId, isLive } = this.props;
            const { isPlaying } = this.state;
            trackingService.track({
              actionOrigin: WIDGET_EVENTS.ACTION_ORIGIN.FLOATING_ACTION_BUTTON,
              eventType: WIDGET_EVENTS.ON_PLAY,
              sourceApp: WIDGET_SOURCE_APPS.FAB,
              orgId,
              widgetId,
              showId,
              isLive,
              isPlaying,
              videoCurrentTime: Math.floor(this._player.currentTime),
            });
          } catch (error) {
            console.log(error);
            this.setupFadeTimer();
          }
          hasOnPlayEmitted = true;
          playStartedTime = this._player.currentTime;
        }
      });

      // Track on-progress event after 3 seconds
      this._player.addEventListener('timeupdate', () => {
        if (!hasOnProgressEmitted && playStartedTime) {
          const timeElapsed = this._player.currentTime - playStartedTime;
          if (timeElapsed >= 3) {
            const { showId, widgetId, orgId, isLive } = this.props;
            const { isPlaying } = this.state;
            trackingService.track({
              actionOrigin: WIDGET_EVENTS.ACTION_ORIGIN.FLOATING_ACTION_BUTTON,
              eventType: WIDGET_EVENTS.ON_PROGRESS,
              sourceApp: WIDGET_SOURCE_APPS.FAB,
              orgId,
              widgetId,
              showId,
              isLive,
              isPlaying,
              videoCurrentTime: Math.floor(this._player.currentTime),
            });
            hasOnProgressEmitted = true;
          }
        }
      });

      this._player.load();
    }
  }

  onClickPlay() {
    const { onClickPlay, isLive, showId, widgetId, orgId } = this.props;
    const { isPlaying } = this.state;

    trackingService.track({
      actionOrigin: WIDGET_EVENTS.ACTION_ORIGIN.FLOATING_ACTION_BUTTON,
      eventType: WIDGET_EVENTS.ON_RESUME,
      sourceApp: WIDGET_SOURCE_APPS.FAB,
      orgId,
      widgetId,
      showId,
      isLive,
      isPlaying,
      videoCurrentTime: this._player ? Math.floor(this._player.currentTime) : 0,
    });

    trackingService.track({
      actionOrigin: WIDGET_EVENTS.ACTION_ORIGIN.FLOATING_ACTION_BUTTON,
      sourceApp: WIDGET_SOURCE_APPS.FAB,
      eventType: WIDGET_EVENTS.ON_INTERACTION,
      interactionType: WIDGET_EVENTS.INTERACTION_TYPE.OPEN_PLAYER,
      orgId,
      widgetId,
      showId,
      isLive,
      isPlaying,
      videoCurrentTime: this._player ? Math.floor(this._player.currentTime) : 0,
    });

    this.setState({ presentCallToAction: false });
    if (typeof onClickPlay === 'function') {
      this.cleanupPlayer();
      onClickPlay();
    }
  }

  onClickClose(event) {
    event.stopPropagation();
    const { onClickClose, orgId, widgetId, showId, isCondensed, isLive } = this.props;
    const { isPlaying } = this.state;

    trackingService.track({
      actionOrigin: WIDGET_EVENTS.ACTION_ORIGIN.FLOATING_ACTION_BUTTON,
      eventType: WIDGET_EVENTS.ON_INTERACTION,
      sourceApp: WIDGET_SOURCE_APPS.FAB,
      interactionType: trackingService.buildTrackingEventName(
        trackingService.getModule(isCondensed, this._playerTimeout),
        WIDGET_EVENTS.INTERACTION_TYPE.CLOSE,
      ),
      orgId,
      widgetId,
      showId,
      isLive,
      isPlaying,
      videoCurrentTime: Math.floor(this._player.currentTime),
    });

    // When closing the FAB, emit a stop event
    trackingService.track({
      actionOrigin: WIDGET_EVENTS.ACTION_ORIGIN.FLOATING_ACTION_BUTTON,
      eventType: WIDGET_EVENTS.ON_STOP,
      sourceApp: WIDGET_SOURCE_APPS.FAB,
      orgId,
      widgetId,
      showId,
      isLive,
      isPlaying,
      videoCurrentTime: Math.floor(this._player.currentTime),
    });

    if (typeof onClickClose === 'function') {
      this.cleanupPlayer();
      onClickClose();
    }
  }

  onClickCondense(event) {
    event.stopPropagation();
    const { onClickCondense, orgId, widgetId, showId, isCondensed, isLive } = this.props;

    trackingService.track({
      actionOrigin: WIDGET_EVENTS.ACTION_ORIGIN.FLOATING_ACTION_BUTTON,
      eventType: WIDGET_EVENTS.ON_INTERACTION,
      interactionType: trackingService.buildTrackingEventName(
        trackingService.getModule(isCondensed, this._playerTimeout),
        WIDGET_EVENTS.INTERACTION_TYPE.CONDENSE,
      ),
      orgId,
      widgetId,
      showId,
      isLive,
    });

    if (typeof onClickCondense === 'function') {
      this.cleanupPlayer();
      onClickCondense();
    }
  }

  onKeyDownPlay(e) {
    isActionKey(e) && this.onClickPlay();
  }

  onKeyDownCondense(e) {
    isActionKey(e) && this.onClickCondense(e);
  }

  onKeyDownClose(e) {
    isActionKey(e) && this.onClickClose(e);
  }

  render() {
    const {
      theme,
      image,
      isLive,
      isVisible,
      onClickClose,
      isCondensed,
      onClickCondense,
      previewOrientation,
      onKeyDownCondense,
      title,
      shouldUseLightWeightPlayer,
    } = this.props;

    const { canShowUI, presentCallToAction, broadcastOrientation, hasError } = this.state;

    if (hasError) {
      return null;
    }

    let widgetClassNames = 'floating-block';

    if ((previewOrientation === 'LANDSCAPE' && !broadcastOrientation) || broadcastOrientation === 'LANDSCAPE') {
      widgetClassNames += ' landscape-orientation';
    }

    if (isVisible) widgetClassNames += ' visible';
    if (presentCallToAction) widgetClassNames += ' fader-active';

    if (isCondensed) {
      let condensedClassNames = 'floating-block-condensed';
      if (isVisible) condensedClassNames += ' visible';
      return (
        <div
          role="button"
          tabIndex="0"
          id="floating-widget"
          className={condensedClassNames}
          onClick={this.onClickPlay}
          onKeyPress={this.onKeyDownPlay}
        >
          <div className="floating-ball">{theme.statusLabels.live}</div>
        </div>
      );
    }

    const isPlayerVisible = isVisible && (isLive || shouldUseLightWeightPlayer);

    // The preview (poster image) of recorded show is not available for recorded shows
    // So we show the video as soon as it is available without any preview
    const isPreviewVisible = isVisible && !shouldUseLightWeightPlayer;
    const showCloseOrCondense = isPreviewVisible || canShowUI;
    const graphicClassNames = `graphic${isPreviewVisible ? '' : ' without-background'}`;

    return (
      <div id="floating-widget" className={widgetClassNames}>
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
        <div
          aria-label={`Open player with show ${title}`}
          className={graphicClassNames}
          onClick={this.onClickPlay}
          onKeyPress={this.onKeyDownPlay}
        >
          {isPreviewVisible && <img alt="" src={image} />}
          <div
            ref={this._playerRef}
            className="player"
            style={{ visibility: isPlayerVisible ? 'visible' : 'hidden' }}
          />
          <div className="fader" />
          {isLive && <div className="live-indicator" data-test-id="fabLiveIndicator">{theme.statusLabels.live}</div>}
          {showCloseOrCondense && (onClickCondense || onKeyDownCondense) && (
            <div
              role="button"
              tabIndex="0"
              className="condense"
              onClick={this.onClickCondense}
              onKeyDown={this.onKeyDownCondense}
            >
              <img alt="Condense widget" src={condenseIcon} />
            </div>
          )}
          {showCloseOrCondense && onClickClose && (
            <div
              role="button"
              tabIndex="0"
              aria-label="close"
              className="close"
              onClick={this.onClickClose}
              onKeyDown={this.onKeyDownClose}
            >
              <img alt="Close widget" src={closeIcon} />
            </div>
          )}
          {theme.floatingWidget && theme.floatingWidget.continueWatchingLabel ? (
            <div
              className="cta-text"
              data-test-id="fabCta"
              role="button"
              tabIndex="0"
              onClick={this.onClickPlay}
              onKeyDown={this.onKeyDownPlay}
              aria-label={`Open player with show ${title}`}
            >
              {theme.floatingWidget.continueWatchingLabel}
            </div>
          ) : (
            <div
              className="cta"
              data-test-id="fabCta"
              role="button"
              tabIndex="0"
              onClick={this.onClickPlay}
              onKeyDown={this.onKeyDownPlay}
              aria-label={`Open player with show ${title}`}
            >
              <img alt="Play show" src={playIcon} />
            </div>
          )}
        </div>
      </div>
    );
  }
}
